import apiClient, { PaginatedResponse } from '@/ApiClient/ApiClient'
import { defaultPageSize } from '@/services/services-config'
import { Media, SignatureResponse } from '@/types/interfaces'
import { saveAs } from 'file-saver'

export const MediaServices = {
  // ------------------------------------------------
  // List media services
  // ------------------------------------------------
  listMedia: async (params?: {
    q?: string
    pageIndex?: number
    pageSize?: number
  }): Promise<PaginatedResponse<Media>> => {
    const response = await apiClient.get<Media[]>('/media', {
      params: {
        s: params?.q,
        page: params?.pageIndex || 1,
        take: params?.pageSize || defaultPageSize,
      },
      paginated: true,
    })
    return response
  },

  // ------------------------------------------------
  // Get media by ID
  // ------------------------------------------------
  getMediaById: async (id: number): Promise<Media> => {
    const response = await apiClient.get<Media>(`/media/${id}`)
    return response
  },

  // ------------------------------------------------
  // Get media by process ID
  // ------------------------------------------------
  getMediaByProcessId: async (
    processId: number,
    params?: {
      pageIndex?: number
      pageSize?: number
      source?: 'media' | 'user_media'
    }
  ): Promise<PaginatedResponse<Media>> => {
    const response = await apiClient.get<Media[]>('/media/view-media-files', {
      params: {
        processId: processId,
        page: params?.pageIndex || 1,
        take: params?.pageSize || defaultPageSize,
        source: params?.source,
      },
      paginated: true,
    })
    return response
  },

  // ------------------------------------------------
  // Download media file
  // ------------------------------------------------
  downloadMedia: async (id: number, filename?: string, source?: string): Promise<void> => {
    try {
      const response = (await apiClient.get(`/media/${id}/download`, {
        responseType: 'blob',
        includeHeaders: true,
        params: {
          source: source,
        },
      })) as { data: Blob; headers: Record<string, string> }

      const blob = response.data

      // Use filename from headers if available, otherwise use provided filename or default
      const disposition = response.headers['content-disposition']
      let downloadFilename = filename

      if (disposition && disposition.includes('filename=')) {
        const filenameMatch = disposition.match(/filename="?([^"]+)"?/)
        if (filenameMatch) {
          downloadFilename = filenameMatch[1]
        }
      }

      if (!downloadFilename) {
        downloadFilename = `media_${id}`
      }

      saveAs(blob, downloadFilename)
    } catch (error) {
      console.error('Error downloading media:', error)
      throw error
    }
  },

  // ------------------------------------------------
  // Preview media file (get blob URL for preview)
  // ------------------------------------------------
  previewMedia: async (id: number, source?: string): Promise<{ url: string; type: string }> => {
    try {
      const response = (await apiClient.get(`/media/${id}/download`, {
        responseType: 'blob',
        includeHeaders: true,
        params: {
          source: source,
        },
      })) as { data: Blob; headers: Record<string, string> }

      const contentType = response.headers['content-type'] || 'application/octet-stream'
      const blob = response.data
      const url = URL.createObjectURL(blob)

      return { url, type: contentType }
    } catch (error) {
      console.error('Error previewing media:', error)
      throw error
    }
  },

  // ------------------------------------------------
  // Get media file info (filename only, no download)
  // ------------------------------------------------
  /**
   * Ottiene solo le informazioni del file (filename) senza scaricarlo.
   * Usa una richiesta HEAD per ottenere solo gli header.
   *
   * @param id - ID del file
   * @param source - Tipo di sorgente ('media' o 'user_media', default: 'media')
   * @returns Nome del file estratto dall'header Content-Disposition
   */
  getMediaFileName: async (id: number, source?: 'media' | 'user_media'): Promise<string> => {
    try {
      const response = (await apiClient.request({
        method: 'HEAD',
        url: `/media/${id}/download`,
        includeHeaders: true,
        params: {
          source: source,
        },
      })) as { headers: Record<string, string> }

      // Estrae il filename dall'header Content-Disposition
      const disposition = response.headers['content-disposition']
      let filename = source === 'user_media' ? `file_${id}` : `media_${id}`

      if (disposition && disposition.includes('filename=')) {
        const filenameMatch = disposition.match(/filename="?([^"]+)"?/)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }

      return filename
    } catch (error) {
      console.error('Error getting media file name:', error)
      // Fallback al nome di default in caso di errore
      return source === 'user_media' ? `file_${id}` : `media_${id}`
    }
  },

  // ------------------------------------------------
  // Get media file with multiple modes (unified method)
  // ------------------------------------------------
  /**
   * Recupera un file e gestisce diverse modalità di risposta.
   *
   * @param id - ID del file
   * @param mode - Modalità ('url', 'download', 'auto', 'blob')
   * @param source - Tipo di sorgente ('media' o 'user_media', default: 'media')
   * @returns Oggetto con url, tipo o blob a seconda della modalità
   */
  getMediaFile: async (
    id: number,
    mode: 'url' | 'download' | 'auto' | 'blob' = 'url',
    source?: 'media' | 'user_media'
  ): Promise<{ url: string; type: string } | { type: string } | { blob: Blob; type: string }> => {
    try {
      const response = (await apiClient.get(`/media/${id}/download`, {
        responseType: 'blob',
        includeHeaders: true,
        params: {
          source: source,
        },
      })) as { data: Blob; headers: Record<string, string> }

      const contentType = response.headers['content-type'] || 'application/octet-stream'
      const blob = response.data

      // Estrae il filename dall'header Content-Disposition
      const disposition = response.headers['content-disposition']
      let filename = source === 'user_media' ? `file_${id}` : `media_${id}`
      if (disposition && disposition.includes('filename=')) {
        const filenameMatch = disposition.match(/filename="?([^"]+)"?/)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }

      switch (mode) {
        case 'url':
          // Sempre preview tramite url
          return { url: URL.createObjectURL(blob), type: contentType }
        case 'download':
          // Forza download
          saveAs(blob, filename)
          return { type: contentType }
        case 'auto':
          // Se PDF preview, altrimenti download
          if (contentType === 'application/pdf') {
            const url = URL.createObjectURL(blob)
            window.open(url, '_blank')
            return { type: contentType }
          } else {
            saveAs(blob, filename)
            return { type: contentType }
          }
        case 'blob':
          // Restituisce direttamente il blob
          return { blob, type: contentType }
        default:
          // Fallback: preview
          return { url: URL.createObjectURL(blob), type: contentType }
      }
    } catch (error) {
      console.error('Error getting media file:', error)
      throw error
    }
  },

  // ------------------------------------------------
  // Delete media
  // ------------------------------------------------
  deleteMedia: async (id: number): Promise<void> => {
    await apiClient.delete(`/media/${id}`)
  },

  // ------------------------------------------------
  // Save media process (for PDF generation)
  // ------------------------------------------------
  saveMediaProcess: async (
    processId: number,
    base64Data: string,
    type?: 'signWeb' | 'tcSign',
    metadata?: any
  ): Promise<SignatureResponse> => {
    const response = await apiClient.post<SignatureResponse>('/media/save-media-process', {
      processId,
      mediaData: base64Data,
      type,
      metadata: metadata,
    })
    return response
  },
}
